import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import DashboardLayout from '../../components/admin/DashboardLayout';
import MediaManager from '../../components/admin/MediaManager';
import { Trash2, Plus, Upload, X, Loader2, Pencil, Image as ImageIcon } from 'lucide-react';
import { useNotification } from '../../context/NotificationContext';
import DeleteConfirmationModal from '../../components/admin/DeleteConfirmationModal'; // New import
import ReactQuill from 'react-quill-new';
// import 'react-quill-new/dist/quill.snow.css';

const GalleryManager = () => {
  const { addNotification } = useNotification();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const fileInputRef = useRef(null);
  
  // Delete Confirmation State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [formData, setFormData] = useState({
    caption: '',
    caption_en: '',
    description: '',
    description_en: '',
    file: null,
    existing_image: ''
  });

  const isVideo = (url) => {
    if (!url) return false;
    return url.match(/\.(mp4|webm|ogg|mov|avi)$/i);
  };

  const fetchImages = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL || 'https://konveksiimoonusel.com/api'}/admin/gallery.php`);
      setImages(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchImages();
  }, []);

  const handleFileChange = (e) => {
    setFormData({ ...formData, file: e.target.files[0], existing_image: '' });
  };

  const handleMediaSelect = (url) => {
    setFormData(prev => ({
      ...prev,
      existing_image: url,
      file: null
    }));
    setShowMediaModal(false);
  };

  const handleEdit = (img) => {
    setFormData({
      caption: img.caption_id || '',
      caption_en: img.caption_en || '',
      description: img.description_id || '',
      description_en: img.description_en || '',
      file: null,
      existing_image: img.full_url || '',
      category_id: img.category_id || ''
    });
    setEditingId(img.id);
    setShowModal(true);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    // If editing, file/existing_image is optional (keep old). If creating, one is required.
    if (!editingId && !formData.file && !formData.existing_image) return;

    setUploading(true);
    const data = new FormData();
    if (formData.file) {
      data.append('image', formData.file);
    } else if (formData.existing_image) {
      data.append('existing_image', formData.existing_image);
    }

    data.append('caption_id', formData.caption);
    data.append('caption_en', formData.caption_en);
    data.append('description_id', formData.description);
    data.append('description_en', formData.description_en);
    if (editingId) data.append('id', editingId);

    try {
      await axios.post(`${process.env.REACT_APP_API_URL || 'https://konveksiimoonusel.com/api'}/admin/gallery.php`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData({ caption: '', caption_en: '', description: '', description_en: '', file: null, existing_image: '' });
      setEditingId(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      setShowModal(false);
      addNotification(editingId ? 'Gambar berhasil diupdate!' : 'Upload gambar berhasil!', 'success');
      fetchImages();
    } catch (err) {
      addNotification('Gagal menyimpan gambar', 'error');
    }
    finally {
      setUploading(false);
    }
  };

  // Trigger Delete Modal
  const handleDelete = (id) => {
    setDeleteTargetId(id);
    setShowDeleteModal(true);
  };

  // Actual Delete Action
  const confirmDelete = async () => {
    if (!deleteTargetId) return;
    setDeleting(true);
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL || 'https://konveksiimoonusel.com/api'}/admin/gallery.php?id=${deleteTargetId}`);
      setImages(images.filter(img => img.id !== deleteTargetId));
      addNotification('Gambar berhasil dihapus', 'success');
      setShowDeleteModal(false);
      setDeleteTargetId(null);
    } catch (err) {
      addNotification('Gagal menghapus gambar', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const styles = {
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem'
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: '700'
    },
    addBtn: {
      backgroundColor: '#111',
      color: '#fff',
      padding: '0.75rem 1.5rem',
      borderRadius: '0.5rem',
      display: 'flex',
      gap: '0.5rem',
      alignItems: 'center',
      border: 'none',
      cursor: 'pointer',
      fontWeight: '600'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
      gap: '1.5rem'
    },
    card: {
      backgroundColor: '#fff',
      borderRadius: '0.75rem',
      overflow: 'hidden',
      border: '1px solid #e5e7eb',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    imgContainer: {
      height: '150px',
      overflow: 'hidden',
      backgroundColor: '#f3f4f6'
    },
    img: {
      width: '100%',
      height: '100%',
      objectFit: 'cover'
    },
    cardBody: {
      padding: '1rem'
    },
    caption: {
      fontSize: '0.875rem',
      marginBottom: '0.5rem',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      fontWeight: '500'
    },
    deleteBtn: {
      color: '#ef4444',
      background: '#fee2e2',
      border: 'none',
      padding: '0.4rem',
      borderRadius: '0.4rem',
      cursor: 'pointer',
      width: '100%',
      display: 'flex',
      justifyContent: 'center'
    },
    // Modal Styles
    modalOverlay: {
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 50
    },
    modal: {
      backgroundColor: '#fff',
      padding: '2rem',
      borderRadius: '1rem',
      width: '90%',
      maxWidth: '600px',
      maxHeight: '90vh',
      overflowY: 'auto'
    },
    inputGroup: {
      marginBottom: '1rem'
    },
    input: {
      width: '100%',
      padding: '0.75rem',
      border: '1px solid #e5e7eb',
      borderRadius: '0.5rem',
      marginTop: '0.5rem'
    },
    label: {
      fontSize: '0.9rem',
      fontWeight: '600'
    },
    modalActions: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '1rem',
      marginTop: '1.5rem'
    }
  };

  return (
    <DashboardLayout>
      <MediaManager 
        isOpen={showMediaModal} 
        onClose={() => setShowMediaModal(false)} 
        onSelect={handleMediaSelect}
        allowVideo={true}
      />

      <div style={styles.header}>
        <h1 style={styles.title}>Manajer Galeri</h1>
        <button style={styles.addBtn} onClick={() => {
          setFormData({ caption: '', caption_en: '', description: '', description_en: '', file: null, existing_image: '' });
          setEditingId(null);
          setShowModal(true);
        }}>
          <Plus size={20} /> Tambah Media
        </button>
      </div>

      {loading ? (
        <div>Memuat...</div>
      ) : (
        <div style={styles.grid}>
          {images.map(img => (
            <div key={img.id} style={styles.card}>
              <div style={styles.imgContainer}>
                {isVideo(img.full_url) ? (
                  <video src={img.full_url} style={styles.img} controls muted />
                ) : (
                  <img src={img.full_url} alt={img.caption_id} style={styles.img} />
                )}
              </div>
              <div style={styles.cardBody}>
                <p style={styles.caption}>{img.caption_id || 'Tanpa Judul'}</p>
                <div style={{display: 'flex', gap: '0.5rem'}}>
                  <button style={{...styles.deleteBtn, background: '#eff6ff', color: '#2563eb'}} onClick={() => handleEdit(img)}>
                    <Pencil size={18} />
                  </button>
                  <button style={styles.deleteBtn} onClick={() => handleDelete(img.id)}>
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload/Edit Modal */}
      {showModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div style={{display:'flex', justifyContent:'space-between', marginBottom:'1.5rem'}}>
              <h3>{editingId ? 'Edit Media' : 'Upload Media'}</h3>
              <button onClick={() => { setShowModal(false); setEditingId(null); setFormData({caption: '', caption_en: '', description: '', description_en: '', file: null}); }} style={{border:'none', background:'none', cursor:'pointer'}}>
                <X />
              </button>
            </div>
            
            <form onSubmit={handleUpload}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Judul</label>
                <input 
                  style={styles.input}
                  type="text" 
                  value={formData.caption}
                  onChange={e => setFormData({...formData, caption: e.target.value})}
                  placeholder="Judul media..."
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>Deskripsi (Rich Text)</label>
                <div style={{marginTop: '0.5rem', height: '200px', marginBottom: '3rem'}}>
                    <ReactQuill 
                        theme="snow"
                        value={formData.description}
                        onChange={(content) => setFormData({...formData, description: content})}
                        style={{height: '150px'}}
                    />
                </div>
              </div>
              
              <div style={styles.inputGroup}>
                <label style={styles.label}>File Media (Gambar/Video)</label>
                
                <div style={{border:'1px solid #e5e7eb', borderRadius:'0.5rem', padding:'0.5rem'}}>
                  <div 
                    onClick={() => setShowMediaModal(true)}
                    style={{
                      padding: '1.5rem', border: '2px dashed #d1d5db', borderRadius: '0.5rem', 
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', 
                      cursor: 'pointer', backgroundColor: '#f9fafb', color: '#6b7280'
                    }}
                  >
                    <ImageIcon size={24} />
                    <span style={{marginTop:'0.5rem', fontSize:'0.85rem', fontWeight:'500'}}>
                      {formData.existing_image ? 'Ganti Media' : 'Pilih Media'}
                    </span>
                  </div>
                  
                  {formData.existing_image && (
                    <div style={{marginTop:'0.5rem'}}>
                      {isVideo(formData.existing_image) ? (
                        <video 
                          src={formData.existing_image} 
                          style={{width:'100%', height:'150px', objectFit:'cover', borderRadius:'0.5rem'}} 
                          controls
                        />
                      ) : (
                        <img 
                          src={formData.existing_image} 
                          alt="Preview" 
                          style={{width:'100%', height:'150px', objectFit:'cover', borderRadius:'0.5rem'}} 
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div style={styles.modalActions}>
                <button 
                  type="button" 
                  onClick={() => { setShowModal(false); setEditingId(null); setFormData({caption: '', caption_en: '', description: '', description_en: '', file: null}); }}
                  style={{...styles.addBtn, backgroundColor: '#e5e7eb', color: '#000'}}
                >
                  Batal
                </button>
                <button 
                  type="submit" 
                  style={{...styles.addBtn, opacity: uploading ? 0.7 : 1}}
                  disabled={uploading}
                >
                  {uploading ? <Loader2 className="animate-spin" /> : (editingId ? <Pencil size={20} /> : <Upload size={20} />)}
                  {uploading ? 'Menyimpan...' : (editingId ? 'Simpan Perubahan' : 'Upload')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        isConfirming={deleting}
        targetName={images.find(img => img.id === deleteTargetId)?.caption_id || 'gambar ini'}
      />
    </DashboardLayout>
  );
};

export default GalleryManager;
